import React, { useEffect, useState, useCallback } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/AppConfig';
import Papa from 'papaparse';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Grid, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';

function getHeaders() {
  const t = sessionStorage.getItem('sessionToken');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

function latencyColor(ms) {
  if (ms <= 20) return 'success';
  if (ms <= 100) return 'warning';
  return 'error';
}

function LatencyChip({ ms }) {
  if (ms == null) return '—';
  return (
    <Chip label={`${ms.toFixed(1)}`} size="small" color={latencyColor(ms)}
      variant={ms > 100 ? 'filled' : 'outlined'}
      sx={{ fontWeight: ms > 100 ? 700 : 400, minWidth: 65, fontFamily: 'monospace' }} />
  );
}

function toIST(date) {
  // Convert to IST (UTC+5:30) and format as datetime-local string
  const ist = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
  return ist.toISOString().slice(0, 16);
}
function getDefaultFrom() {
  const d = new Date(Date.now() - 60 * 60 * 1000); // 1 hour ago
  return toIST(d);
}
function getDefaultTo() {
  return toIST(new Date());
}

function ApiLatency() {
  const [stats, setStats] = useState([]);
  const [liveStats, setLiveStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('');
  const [from, setFrom] = useState(getDefaultFrom());
  const [to, setTo] = useState(getDefaultTo());
  const [sortBy, setSortBy] = useState('p95');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch both historical + live in one call, merge for display
  const fetchStats = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams({ sort: sortBy });
      if (filter.trim()) p.append('filter', filter.trim());
      if (from) p.append('from', from);
      if (to) p.append('to', to);

      const res = await fetch(`${BASE_URL}/admin/api-latency/stats?${p}`, { credentials: 'include', headers: getHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.message || 'Failed'); return; }

      const dbEndpoints = data.data?.endpoints ?? [];
      const live = data.data?.live?.endpoints ?? [];

      // Merge: for endpoints only in live (not yet in DB), add them. For ones in both, DB is authoritative.
      const dbSet = new Set(dbEndpoints.map(e => e.endpoint));
      const liveOnly = live.filter(e => !dbSet.has(e.endpoint));
      const merged = [...dbEndpoints, ...liveOnly];

      // Re-sort
      const validSorts = ['p50', 'p75', 'p95', 'p99', 'avg', 'max', 'min', 'sampleCount'];
      if (validSorts.includes(sortBy)) {
        merged.sort((a, b) => sortOrder === 'desc'
          ? (b[sortBy] ?? 0) - (a[sortBy] ?? 0)
          : (a[sortBy] ?? 0) - (b[sortBy] ?? 0));
      }

      setStats(merged);
      setLiveStats(live);
      setSummary(data.data?.summary ?? null);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [filter, from, to, sortBy, sortOrder]);

  useEffect(() => { fetchStats(); }, [fetchStats]);


  const handleReset = async () => {
    if (!window.confirm('Clear all latency data (Redis + DB)?')) return;
    try {
      await fetch(`${BASE_URL}/admin/api-latency/reset?purgeDB=true`, { method: 'POST', credentials: 'include', headers: getHeaders() });
      setSuccess('Latency data cleared');
      fetchStats();
    } catch (e) { setError(e.message); }
  };

  const exportToCSV = () => {
    const csvData = stats.map(item => ({
      Endpoint: item.endpoint,
      Requests: item.sampleCount,
      P50: item.p50,
      P75: item.p75,
      P95: item.p95,
      P99: item.p99,
      Average: item.avg,
      Min: item.min,
      Max: item.max,
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'api_latency_export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sortOptions = [
    { value: 'p95', label: 'P95' }, { value: 'p99', label: 'P99' },
    { value: 'p75', label: 'P75' }, { value: 'p50', label: 'P50' },
    { value: 'avg', label: 'Average' }, { value: 'max', label: 'Max' },
    { value: 'sampleCount', label: 'Most Requests' },
  ];

  const totalEndpoints = stats.length;
  const over100 = stats.filter(s => s.p95 > 100).length;
  const over20 = stats.filter(s => s.p95 > 20 && s.p95 <= 100).length;
  const under20 = stats.filter(s => s.p95 <= 20).length;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header"><div className="row"><div className="col-sm-6"><h3>API Latency Dashboard</h3></div></div></div>
        </div>
        <div className="container-fluid"><div className="row"><div className="col-sm-12">
          <Card><CardContent>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                  <Typography variant="h4" fontWeight={700}>{totalEndpoints}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Endpoints</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                  <Typography variant="h4" fontWeight={700} color="success.main">{under20}</Typography>
                  <Typography variant="caption" color="text.secondary">Under 20ms (P95)</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: over20 > 0 ? '#fff3e0' : '#e8f5e9' }}>
                  <Typography variant="h4" fontWeight={700} color={over20 > 0 ? 'warning.main' : 'success.main'}>{over20}</Typography>
                  <Typography variant="caption" color="text.secondary">20–100ms (P95)</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: over100 > 0 ? '#ffebee' : '#e8f5e9' }}>
                  <Typography variant="h4" fontWeight={700} color={over100 > 0 ? 'error.main' : 'success.main'}>{over100}</Typography>
                  <Typography variant="caption" color="text.secondary">Over 100ms (P95)</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField label="From (IST)" size="small" type="datetime-local" value={from}
                onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 220 }} />
              <TextField label="To (IST)" size="small" type="datetime-local" value={to}
                onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 220 }} />
              <TextField label="Filter endpoint" size="small" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder="e.g. /feeds or POST" sx={{ width: 200 }} />
              <TextField label="Sort by" size="small" select value={sortBy} onChange={e => setSortBy(e.target.value)}
                SelectProps={{ native: true }} sx={{ width: 130 }}>
                {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </TextField>
              <TextField label="Order" size="small" select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
                SelectProps={{ native: true }} sx={{ width: 130 }}>
                <option value="desc">Highest first</option>
                <option value="asc">Lowest first</option>
              </TextField>
              <Button variant="contained" size="small" onClick={fetchStats} disabled={loading}>
                {loading ? <CircularProgress size={18} /> : 'Search'}
              </Button>
              <Button variant="outlined" size="small" onClick={exportToCSV} disabled={stats.length === 0}>
                Export to CSV
              </Button>
              <Button variant="outlined" size="small" color="error" onClick={handleReset}>Reset</Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              <Chip size="small" label="≤20ms" color="success" variant="outlined" sx={{ mx: 0.5 }} />
              <Chip size="small" label="20–100ms" color="warning" variant="outlined" sx={{ mx: 0.5 }} />
              <Chip size="small" label=">100ms" color="error" variant="filled" sx={{ mx: 0.5 }} />
              {' '}· All values in ms
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Table */}
            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Endpoint</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Requests</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>P50</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>P75</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>P95</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>P99</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Avg</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Min</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Max</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.length === 0 ? (
                      <TableRow><TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No latency data for this time range.</Typography>
                      </TableCell></TableRow>
                    ) : stats.map((s, i) => (
                      <TableRow key={i} sx={{ bgcolor: s.p95 > 100 ? '#fff8f8' : 'inherit' }}>
                        <TableCell>
                          <Tooltip title={s.endpoint} arrow>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {s.endpoint}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center"><Typography variant="caption">{s.sampleCount}</Typography></TableCell>
                        <TableCell align="center"><LatencyChip ms={s.p50} /></TableCell>
                        <TableCell align="center"><LatencyChip ms={s.p75} /></TableCell>
                        <TableCell align="center"><LatencyChip ms={s.p95} /></TableCell>
                        <TableCell align="center"><LatencyChip ms={s.p99} /></TableCell>
                        <TableCell align="center"><LatencyChip ms={s.avg} /></TableCell>
                        <TableCell align="center"><Typography variant="caption">{s.min?.toFixed(1)}</Typography></TableCell>
                        <TableCell align="center"><Typography variant="caption" color={s.max > 100 ? 'error' : 'text.secondary'}>{s.max?.toFixed(1)}</Typography></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

          </CardContent></Card>
        </div></div></div>
      </div>
    </Component>
  );
}

export default ApiLatency;
